/**
 * TDMA (Thomas Algorithm) — Tridiagonal Matrix Algorithm.
 *
 * Solves the system Ax = d where A is tridiagonal:
 *   a[i]·x[i-1] + b[i]·x[i] + c[i]·x[i+1] = d[i]
 *
 * O(N) time complexity, O(N) space.
 * Used for pressure correction and energy equations along each pipe.
 */

/**
 * Solve tridiagonal system using Thomas algorithm.
 *
 * @param a Sub-diagonal coefficients [n] (a[0] unused)
 * @param b Main diagonal coefficients [n]
 * @param c Super-diagonal coefficients [n] (c[n-1] unused)
 * @param d Right-hand side [n]
 * @returns Solution vector x [n]
 */
export function tdma(
  a: number[],
  b: number[],
  c: number[],
  d: number[]
): number[] {
  const n = b.length;
  if (n === 0) return [];
  if (n === 1) return [d[0] / b[0]];

  // Forward sweep — modify c and d
  const c_star = new Float64Array(n);
  const d_star = new Float64Array(n);

  c_star[0] = c[0] / b[0];
  d_star[0] = d[0] / b[0];

  for (let i = 1; i < n; i++) {
    const m = a[i] / (b[i] - a[i] * c_star[i - 1]);
    c_star[i] = c[i] / (b[i] - a[i] * c_star[i - 1]);
    d_star[i] = (d[i] - a[i] * d_star[i - 1]) / (b[i] - a[i] * c_star[i - 1]);
  }

  // Back substitution
  const x = new Float64Array(n);
  x[n - 1] = d_star[n - 1];

  for (let i = n - 2; i >= 0; i--) {
    x[i] = d_star[i] - c_star[i] * x[i + 1];
  }

  return Array.from(x);
}

/**
 * Solve tridiagonal system with known boundary values.
 * The first and last equations are replaced by x[0] = x_left, x[n-1] = x_right.
 *
 * @param a Sub-diagonal [n]
 * @param b Main diagonal [n]
 * @param c Super-diagonal [n]
 * @param d RHS [n]
 * @param x_left Left boundary value
 * @param x_right Right boundary value
 * @returns Solution vector x [n]
 */
export function tdmaWithBoundaries(
  a: number[],
  b: number[],
  c: number[],
  d: number[],
  x_left: number,
  x_right: number
): number[] {
  const n = b.length;
  if (n <= 2) return [x_left, x_right].slice(0, n);

  // Modify first and last rows for Dirichlet boundaries
  const a2 = [...a];
  const b2 = [...b];
  const c2 = [...c];
  const d2 = [...d];

  // First row: x[0] = x_left
  a2[0] = 0; b2[0] = 1; c2[0] = 0; d2[0] = x_left;

  // Last row: x[n-1] = x_right
  a2[n - 1] = 0; b2[n - 1] = 1; c2[n - 1] = 0; d2[n - 1] = x_right;

  return tdma(a2, b2, c2, d2);
}
